import React, {useState} from "react";
import {useQueryClient} from "react-query";
import {useNavigate, useParams} from "react-router-dom";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {alertStateAtom} from "../../Recoil/Atom/AlertAtom";
import {tokenSelector} from "../../Recoil/Atom/authState";
import {useGetQuery, useRemoveMutation} from "../../hooks/useQueryHooks";
import {ResponseList, ResponseRemove} from "../../models/clientModels/interfaces/Response";
import {
  EQDocument,
  documentTypeName,
  initializeDocumentListParams,
} from "../../models/serverModels/workOrder/EQDocument";

import {
  Box,
  Button,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
} from "@mui/material";
import * as documentApi from "../../api/documentApi/documentApi";
import ActionButtonGroup from "../../components/ActionButtonGroup";
import MWSkeleton from "../../components/MWSkeleton";
import SubTitle from "../../components/SubTitle";
import {documentActionButton} from "../../models/clientModels/data/actionButtonData";
import * as Params from "../../models/clientModels/interfaces/apiParams";
import {SubMenuGroup} from "../../styledComponents/StyledListContainer";
import {PaginationContainer} from "../../styledComponents/StyledPagination";
import {StyledNoneScrollTable, StyledNoneStickyTable} from "../../styledComponents/StyledTable";

const ListContainer = styled(Box)(({theme}) => ({
  minWidth: "1500px",
}));

const DocumentList = () => {
  const {id} = useParams();
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const tokenState = useRecoilValue(tokenSelector);

  const setAlertState = useSetRecoilState(alertStateAtom);

  const [page, setPage] = useState<number>(1);

  const {
    data: documentList,
    isLoading,
    isFetching,
  } = useGetQuery<ResponseList<EQDocument>>({
    key: ["DocumentList", {id: parseInt(id!), token: tokenState, page}],
    api: () =>
      documentApi.getList({
        ...initializeDocumentListParams,
        id: parseInt(id!),
        token: tokenState,
        page,
      }),
  });

  const {mutate: removeMutate} = useRemoveMutation<ResponseRemove, Params.Remove>({
    api: documentApi.remove,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("DocumentList");
      },
    },
  });

  const paginationHandleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  return (
    <ListContainer>
      <SubTitle subTitle={documentTypeName[parseInt(id!)]} />
      <SubMenuGroup>
        <Box></Box>
        <ActionButtonGroup
          data={documentActionButton(() =>
            navigate(`/Document/CreateDocument`, {state: parseInt(id!)})
          )}
        />
      </SubMenuGroup>
      <StyledNoneScrollTable>
        <Table>
          <StyledNoneStickyTable>
            <TableRow>
              <TableCell align="center">#</TableCell>
              <TableCell align="center">설명</TableCell>
              <TableCell align="center">파일명</TableCell>
              <TableCell align="center">등록일</TableCell>
              <TableCell align="center">수정일</TableCell>
              <TableCell align="center">편집</TableCell>
            </TableRow>
          </StyledNoneStickyTable>
          <TableBody>
            {isLoading && isFetching ? (
              <MWSkeleton row={12} column={6} />
            ) : (
              documentList?.items.map((item, index) => (
                <TableRow key={item.uid}>
                  <TableCell align="center">
                    {page === 1
                      ? index + 1
                      : (page - 1) * initializeDocumentListParams.rows! + index + 1}
                  </TableCell>
                  <TableCell align="left">{item.description}</TableCell>
                  <TableCell align="center">{item.fileName}</TableCell>

                  <TableCell align="center">
                    {new Date(item?.entryDate).getFullYear()}년
                    {new Date(item?.entryDate).getMonth() + 1}월{" "}
                    {new Date(item?.entryDate).getDate()}일
                  </TableCell>
                  <TableCell align="center">
                    {new Date(item?.modifyDate).getFullYear()}년
                    {new Date(item?.modifyDate).getMonth() + 1}월{" "}
                    {new Date(item?.modifyDate).getDate()}일
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      style={{marginRight: "12px"}}
                      onClick={() => {
                        navigate(`/Document/EditDocument/${item.uid}`, {
                          state: id,
                        });
                      }}
                    >
                      편집
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={() => {
                        setAlertState({
                          toggle: true,
                          message: "등록된 서식을 삭제하시겠습니까?",
                          execution: () => removeMutate({token: tokenState, id: item.uid}),
                        });
                      }}
                    >
                      삭제
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledNoneScrollTable>
      {/* 페이지네이션 */}
      <PaginationContainer>
        <Pagination
          count={documentList?.maxPage}
          page={page}
          onChange={paginationHandleChange}
          variant="outlined"
          color="primary"
          shape="rounded"
        />
      </PaginationContainer>
    </ListContainer>
  );
};

export default DocumentList;
